import { Link } from "@tanstack/react-router";
import { BadgeCheck, ShieldCheck } from "lucide-react";
import { SITE } from "@/content/site";
import portrait from "@/assets/tamal-portrait-photo.webp?w=96&format=webp";

type Props = {
  /**
   * ISO date the page was last fact-checked / reviewed.
   * Required for the "Last reviewed" line to render — we never fall back
   * to today's date because that would defeat the trust signal.
   */
  reviewedOn?: string;
  /** Override the default credentials line. */
  credentials?: string;
};

/**
 * Small E-E-A-T strip that names the human reviewer of a page,
 * lists their credentials, and links to the about page where the
 * full bio + sameAs profiles live. Pairs with the reviewer fields
 * in the page-level Schema.org markup.
 */
export function ReviewedBy({ reviewedOn, credentials }: Props) {
  const formatted = reviewedOn
    ? new Date(reviewedOn).toLocaleDateString("en", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;
  const creds =
    credentials ??
    "SEO, AEO and GEO consultant · Technical SEO, local SEO and content strategy";

  return (
    <div className="mt-6 inline-flex items-start gap-4 rounded-2xl border border-border bg-card/60 px-5 py-4">
      <img
        src={portrait}
        alt={`${SITE.fullName}, SEO expert`}
        width={44}
        height={44}
        loading="lazy"
        decoding="async"
        className="size-11 rounded-full object-cover shrink-0"
      />
      <div className="text-sm">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-foreground">
          <span className="inline-flex items-center gap-1 text-primary">
            <BadgeCheck size={14} /> Written &amp; reviewed by
          </span>
          <Link to="/about" rel="author" className="font-medium hover:text-primary">
            {SITE.fullName}
          </Link>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{creds}</p>
        {formatted ? (
          <p className="mt-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
            <ShieldCheck size={11} /> Last reviewed {formatted}
          </p>
        ) : null}
      </div>
    </div>
  );
}